import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { FormEvent, MouseEvent, ReactNode, Ref } from "react";
import { Link, useInRouterContext } from "react-router";
import {
  BellRing,
  ChevronRight,
  ClipboardCheck,
  Download,
  FileText,
  Gift,
  Handshake,
  Landmark,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquareText,
  PlugZap,
  RotateCcw,
  Scale,
  ScrollText,
  ShieldCheck,
  UserCog,
  UsersRound,
} from "lucide-react";
import {
  Alert,
  BrandWordmark,
  Button,
  Dialog,
  Field,
  StepIndicator,
  StatusBadge,
  UiProvider,
} from "../../ui";
import { GuidedActionButton } from "./GuidedActionButton";
import type { FieldElement, StatusBadgeVariant } from "../../ui";
import type { Command, ScreenId, ScreenState } from "./model";
import { titles } from "./model";
import { failure } from "./behavior";
import { useCommandState } from "./command-state";
import { ADMIN_NAVIGATION_GROUPS } from "../../app/routes";
import { getAdminRoleLabels } from "../../app/permissions";
import "./workspace.css";

const navigation: readonly { label: string; items: readonly ScreenId[] }[] =
  ADMIN_NAVIGATION_GROUPS.map((group) => ({ label: group.label, items: group.screens }));

const navigationIcons: Partial<Record<ScreenId, ReactNode>> = {
  "04": <LayoutDashboard size={17} />,
  "42": <UsersRound size={17} />,
  "05": <UsersRound size={17} />,
  "07": <ClipboardCheck size={17} />,
  "09": <Landmark size={17} />,
  "11": <RotateCcw size={17} />,
  "12": <Gift size={17} />,
  "13": <MessageSquareText size={17} />,
  "14": <BellRing size={17} />,
  "15": <FileText size={17} />,
  "16": <Handshake size={17} />,
  "17": <PlugZap size={17} />,
  "18": <Scale size={17} />,
  "19": <ScrollText size={17} />,
  "20": <Download size={17} />,
  "21": <UserCog size={17} />,
  "22": <ShieldCheck size={17} />,
  "33": <ClipboardCheck size={17} />,
  "34": <UsersRound size={17} />,
  "35": <ShieldCheck size={17} />,
  "36": <FileText size={17} />,
  "37": <RotateCcw size={17} />,
  "38": <Landmark size={17} />,
  "39": <PlugZap size={17} />,
  "40": <Download size={17} />,
  "41": <Download size={17} />,
};

function AdminPageTransitionSkeleton({ variant }: { readonly variant: "dashboard" | "detail" | "form" | "list" }) {
  return (
    <div className={`admin-transition admin-transition--${variant}`} data-admin-page-transition aria-hidden="true">
      <div className="admin-transition__heading"><span /><span /></div>
      {variant === "dashboard" ? (
        <div className="admin-transition__dashboard">
          <section>{Array.from({ length: 3 }, (_, index) => <div className="admin-transition__task" key={index}><span /><span /><span /></div>)}</section>
          <section className="admin-transition__metrics">{Array.from({ length: 4 }, (_, index) => <div key={index}><span /><span /></div>)}</section>
        </div>
      ) : variant === "detail" || variant === "form" ? (
        <div className="admin-transition__detail">
          <section>{Array.from({ length: variant === "form" ? 5 : 4 }, (_, index) => <div className="admin-transition__detail-row" key={index}><span /><span /></div>)}</section>
          <aside>{Array.from({ length: 4 }, (_, index) => <div className="admin-transition__detail-row" key={index}><span /><span /></div>)}</aside>
        </div>
      ) : (
        <>
          <div className="admin-transition__filters"><span /><span /><span /></div>
          <div className="admin-transition__table">
            <div className="admin-transition__table-head"><span /><span /><span /><span /></div>
            {Array.from({ length: 10 }, (_, index) => <div className="admin-transition__table-row" key={index}><span /><span /><span /><span /></div>)}
          </div>
        </>
      )}
    </div>
  );
}

export function handleAdminLinkNavigation(
  event: MouseEvent<HTMLAnchorElement>,
  navigate: () => void,
) {
  if (
    event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.ctrlKey
    || event.shiftKey
    || event.altKey
    || (event.currentTarget.target && event.currentTarget.target !== "_self")
  ) return;
  event.preventDefault();
  navigate();
}

export function AdminLayout({
  active,
  children,
  onNavigate,
  onLogout,
  identity,
  authorities = [],
  visibleScreens,
  navigationHref,
  routeKey,
  routePath,
  routeDataLoading = false,
}: {
  active: ScreenId;
  identity: { name: string; loginId: string };
  authorities?: readonly string[];
  onLogout: () => void;
  children: ReactNode;
  onNavigate: (screen: ScreenId) => void;
  visibleScreens?: readonly ScreenId[];
  navigationHref?: (screen: ScreenId) => string;
  routeKey?: string;
  routePath?: string;
  routeDataLoading?: boolean;
}) {
  const [menuOpen,setMenuOpen] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const previousRouteKey = useRef(routeKey);
  const transitionTimer = useRef<number | undefined>(undefined);
  const [minimumTransitionElapsed, setMinimumTransitionElapsed] = useState(false);
  const activeNavigation = ({"06":"05","08":"07","10":"09"} as Partial<Record<ScreenId,ScreenId>>)[active] ?? active;
  const transitionVariant = activeNavigation === "04"
    ? "dashboard"
    : routePath?.endsWith("/new") || routePath?.includes("mode=edit")
      ? "form"
      : ["06", "08", "10", "23", "24", "25", "26", "27", "29", "30", "31", "32"].includes(active)
        ? "detail"
        : "list";
  const navigate = (screen: ScreenId) => { onNavigate(screen); setMenuOpen(false); };
  const visible = visibleScreens ? new Set(visibleScreens) : null;
  const roleLabels = getAdminRoleLabels(authorities);

  useLayoutEffect(() => {
    if (!routeKey || previousRouteKey.current === routeKey) return;
    previousRouteKey.current = routeKey;
    if (transitionTimer.current !== undefined) window.clearTimeout(transitionTimer.current);
    setTransitioning(true);
    setMinimumTransitionElapsed(false);
    transitionTimer.current = window.setTimeout(() => setMinimumTransitionElapsed(true), 420);
    return () => {
      if (transitionTimer.current !== undefined) window.clearTimeout(transitionTimer.current);
    };
  }, [routeKey]);

  useEffect(() => {
    if (!transitioning || !minimumTransitionElapsed || routeDataLoading) return;
    setTransitioning(false);
  }, [minimumTransitionElapsed, routeDataLoading, transitioning]);

  useEffect(() => {
    if (transitioning) return;
    const frame = window.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>("[data-admin-page-id]")?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [routeKey, transitioning]);
  const renderNavigation = (id: string, className?: string) => (
    <nav id={id} className={className} aria-label="관리자 업무">
      {navigation
        .map((group) => ({ ...group, items: group.items.filter((item) => visible?.has(item) ?? true) }))
        .filter((group) => group.items.length > 0)
        .map((group) => (
          <section key={group.label} aria-labelledby={`${id}-${group.label}`}>
            <h2 id={`${id}-${group.label}`}>{group.label}</h2>
            <div>
              {group.items.map((screen) => (
                <a
                  aria-current={activeNavigation === screen ? "page" : undefined}
                  href={navigationHref?.(screen) ?? "#"}
                  key={screen}
                  onClick={(event) => handleAdminLinkNavigation(event, () => navigate(screen))}
                >
                  <span className="admin-workspace__navigation-icon" aria-hidden="true">{navigationIcons[screen]}</span>
                  {titles[screen]}
                </a>
              ))}
            </div>
          </section>
        ))}
    </nav>
  );
  return (
    <UiProvider>
      <div className="admin-workspace" data-admin-ui="v2">
      <a className="admin-workspace__skip" href="#admin-main">본문으로 이동</a>
      <aside className="admin-workspace__navigation" aria-label="관리자 주 메뉴">
        <div className="admin-workspace__brand"><BrandWordmark /><strong>관리자 콘솔</strong><span>운영 워크스페이스</span></div>
        {renderNavigation("admin-navigation")}
        <div className="admin-workspace__security"><ShieldCheck aria-hidden="true" size={16} /><span>보안 접속 기록 중</span></div>
      </aside>
      <div className="admin-workspace__body">
        <header className="admin-workspace__header">
          <Button
            aria-controls="admin-mobile-navigation"
            aria-expanded={menuOpen}
            className="admin-workspace__menu"
            iconOnly
            size="small"
            variant="secondary"
            onClick={() => setMenuOpen(true)}
            aria-label={`${titles[activeNavigation]} · 메뉴 열기`}
          >
            <Menu aria-hidden="true" size={18} />
          </Button>
          <div className="admin-workspace__context"><span>관리자 콘솔</span><ChevronRight aria-hidden="true" size={14} /><strong className="admin-workspace__current">{titles[activeNavigation]}</strong></div>
          {roleLabels.length > 0 ? <div className="admin-workspace__authority" role="group" aria-label="계정 역할"><ShieldCheck aria-hidden="true" size={15} /><span>{roleLabels.join(" · ")}</span></div> : null}
          <div className="admin-workspace__identity"><span className="admin-workspace__avatar" aria-hidden="true">{identity.name.slice(0, 1)}</span><div><strong>{identity.name}</strong><span>{identity.loginId}</span></div></div>
          <Button size="small" variant="quiet" leadingIcon={<LogOut aria-hidden="true" size={16} />} onClick={onLogout}>로그아웃</Button>
        </header>
        <main id="admin-main" className="admin-workspace__main" tabIndex={-1} aria-busy={transitioning || undefined}>
          <div className="admin-workspace__route-content" inert={transitioning ? true : undefined}>{children}</div>
          {transitioning ? <><p className="admin-transition__status" role="status">새 페이지를 불러오는 중입니다.</p><AdminPageTransitionSkeleton variant={transitionVariant} /></> : null}
        </main>
      </div>
      <Dialog
        className="admin-workspace__mobile-dialog"
        open={menuOpen}
        title="관리자 메뉴"
        description={`현재 위치 · ${titles[activeNavigation]}`}
        onClose={() => setMenuOpen(false)}
      >
        {renderNavigation("admin-mobile-navigation", "admin-workspace__mobile-navigation")}
      </Dialog>
      </div>
    </UiProvider>
  );
}

export function AuthLayout({ step, children }: { step: "login" | "password" | "totp"; children: ReactNode }) {
  const labels = { login: "관리자 로그인", password: "비밀번호 변경", totp: "TOTP 설정" };
  const setupSteps = [
    { id: "login", label: "로그인" },
    { id: "password", label: "비밀번호 변경" },
    { id: "totp", label: "인증 앱 등록" },
  ];
  return <UiProvider><main className="admin-auth">
    <section className="admin-auth__context" aria-labelledby="admin-auth-context-title">
      <div className="admin-auth__brand"><BrandWordmark /><span>관리자 콘솔</span></div>
      <div className="admin-auth__intro"><p>CarePass Operations</p><h2 id="admin-auth-context-title">안전한 운영을 위한<br />관리자 전용 공간</h2><p>회원·거래·결제·송금 정보를 권한 범위 안에서 확인하고 처리합니다.</p></div>
      <ul className="admin-auth__assurances"><li><span><ShieldCheck aria-hidden="true" size={18} /></span><div><strong>다단계 인증</strong><p>비밀번호와 인증 앱으로 관리자 계정을 보호합니다.</p></div></li><li><span><ScrollText aria-hidden="true" size={18} /></span><div><strong>처리 이력 기록</strong><p>조회와 승인 기록을 관리자 단위로 남깁니다.</p></div></li></ul>
      <p className="admin-auth__security"><ShieldCheck aria-hidden="true" size={15} />허가된 관리자만 접근할 수 있습니다.</p>
    </section>
    <section className="admin-auth__panel"><div className="admin-auth__surface">
      <div className="admin-auth__mobile-brand"><BrandWordmark /><span>관리자 콘솔</span></div>
      <p className="admin-eyebrow">보안 단계 · {labels[step]}</p>
      {step !== "login" ? <StepIndicator currentId={step} items={setupSteps} label="초기 보안 설정" /> : null}
      {children}
    </div></section>
  </main></UiProvider>;
}

export function PageHeader({
  id,
  title,
  description,
  meta,
  action,
  breadcrumbs,
  backAction,
  headingRef,
  focusHeading = true,
  hideBody = false,
}: {
  id: string;
  title: string;
  description: string;
  meta?: ReactNode;
  action?: ReactNode;
  breadcrumbs?: readonly { readonly label: string; readonly href: string; readonly onClick?: () => void }[];
  backAction?: { readonly label: string; readonly href: string; readonly onClick: () => void };
  headingRef?: Ref<HTMLHeadingElement>;
  focusHeading?: boolean;
  hideBody?: boolean;
}) {
  const internalHeadingRef = useRef<HTMLHeadingElement>(null);
  const inRouter = useInRouterContext();
  useEffect(() => {
    if (!focusHeading || headingRef) return;
    internalHeadingRef.current?.focus({ preventScroll: true });
    if (window.scrollY !== 0) window.scrollTo({ top: 0 });
  }, [focusHeading, headingRef]);
  return <header className={`admin-page-header${hideBody && !action && !backAction && !breadcrumbs?.length ? " admin-page-header--compact" : ""}`}>
    <div>
      {breadcrumbs?.length ? <nav className="admin-breadcrumbs" aria-label="현재 위치"><ol>{breadcrumbs.map((item) => <li key={`${item.href}:${item.label}`}>{inRouter ? <Link to={item.href} onClick={item.onClick ? (event) => handleAdminLinkNavigation(event, item.onClick!) : undefined}>{item.label}</Link> : <a href={item.href} onClick={item.onClick ? (event) => handleAdminLinkNavigation(event, item.onClick!) : undefined}>{item.label}</a>}</li>)}</ol></nav> : null}
      <h1 className={hideBody ? "admin-visually-hidden" : undefined} data-admin-page-id={id} ref={headingRef ?? internalHeadingRef} tabIndex={-1}>{title}</h1>
      {description && !hideBody ? <p>{description}</p> : null}
      {meta}
      {backAction ? <a className="admin-page-header__back" href={backAction.href} onClick={(event) => handleAdminLinkNavigation(event, backAction.onClick)}>{backAction.label}</a> : null}
    </div>
    {action && <div className="admin-page-header__action">{action}</div>}
  </header>;
}

export function Panel({ title, description, action, children, className = "" }: { title: string; description?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return <section className={`admin-panel ${className}`.trim()}>
    <header className="admin-panel__header"><div><h2>{title}</h2>{description && <p>{description}</p>}</div>{action}</header>
    <div className="admin-panel__body">{children}</div>
  </section>;
}

export function FilterBar({ children, onSubmit }: { children: ReactNode; onSubmit?: () => void }) {
  return <form className="admin-filter-bar" onSubmit={(event) => { event.preventDefault(); onSubmit?.(); }}>{children}<Button size="small" type="submit">조회</Button></form>;
}

export function updateStringField<T extends Record<string,string>>(
  setter: (update: (current: T) => T) => void,
  key: keyof T,
  value: string,
) {
  setter(current => ({...current,[key]:value}));
}

export function Kpi({ label, value, note }: { label: string; value: ReactNode; note?: string }) {
  return <section className="admin-kpi"><h2>{label}</h2><strong data-numeric>{value}</strong>{note && <p>{note}</p>}</section>;
}

export function StateNotice({ state, onRefresh, onLogin }: { state?: ScreenState; onRefresh?: () => void; onLogin?: () => void }) {
  if (state === "denied") return <Alert persistent variant="danger" title="권한 없음">현재 관리자 권한으로 이 업무를 처리할 수 없습니다. 권한 변경이 필요하면 시스템 관리자에게 요청해 주세요.</Alert>;
  if (state === "expired") return <Alert persistent variant="warning" title="로그인이 만료되었습니다" action={<Button size="small" variant="quiet" onClick={onLogin}>다시 로그인</Button>}>작성 중인 내용은 전송되지 않았습니다.</Alert>;
  if (state === "not-found") return <Alert persistent variant="information" title="대상을 찾을 수 없습니다">삭제되었거나 접근 가능한 범위를 벗어났습니다. 목록에서 다시 선택해 주세요.</Alert>;
  if (state === "conflict") return <Alert persistent variant="warning" title="다른 관리자가 먼저 처리했습니다" action={onRefresh?<Button size="small" variant="quiet" onClick={onRefresh}>최신 상태 조회</Button>:undefined}>완료로 표시하지 않았습니다. 최신 상태를 확인한 뒤 계속해 주세요.</Alert>;
  return null;
}

export function SectionTabs({ items, value, onChange }: { items: readonly string[]; value: string; onChange: (value: string) => void }) {
  return <div className="admin-section-tabs" role="tablist" aria-label="업무 구분">{items.map((item) => <button aria-selected={item === value} key={item} onClick={() => onChange(item)} role="tab" type="button">{item}</button>)}</div>;
}

export function statusBadge(status: string, label?: string) {
  const normalized = status.toUpperCase();
  const variant: StatusBadgeVariant = normalized.includes("FAIL") || normalized.includes("REJECT") || normalized.includes("DISABLED") || normalized === "LOCKED" || normalized === "CANCELED"
    ? "danger" : normalized.includes("COMPLETE") || normalized.includes("APPROVED") || normalized.includes("PAID") || normalized.includes("PUBLISHED") || normalized.includes("SENT") || normalized === "ACTIVE" || normalized === "VERIFIED" || normalized === "CONSUMED"
      ? "success" : normalized.includes("PROCESS") || normalized.includes("REVIEWING")
        ? "processing" : normalized.includes("PENDING") || normalized.includes("READY") || normalized.includes("REQUEST") || normalized.includes("SUBMITTED") || normalized.includes("SCHEDULED")
          ? "waiting" : "neutral";
  const labels: Record<string,string> = { RECEIVED:"접수됨", IN_PROGRESS: "답변 준비 중", APPROVAL_PENDING:"승인 대기", PAID:"지급 완료", UNKNOWN:"결과 확인 중", PAYMENT_UNKNOWN:"결제 결과 확인 중", TRANSFER_UNKNOWN:"송금 결과 확인 중", BASIC: "활성화 전", ACTIVE: "이용 중", LOCKED: "잠김", CLOSED: "종료", SUSPENDED: "일시 중지", DISABLED: "비활성", NONE: "없음", PAST_DUE: "결제 확인 필요", UNDER_REVIEW: "심사 대기", REVIEWING: "심사 중", SUPPLEMENT_REQUIRED: "자료 보완 필요", PAYMENT_PENDING: "회원 카드 결제 대기", TRANSFER_SELECTION_PENDING: "결제 완료 · 송금 미선택", TRANSFER_READY: "송금 대기", TRANSFER_PROCESSING: "송금 처리 중", TRANSFER_FAILED: "송금 실패", CANCELED: "취소", NOT_STARTED: "시작 전", READY: "실행 대기", PROCESSING: "처리 중", FAILED: "처리 실패", REQUESTED: "요청됨", SUBMITTED: "접수됨", ASSIGNED: "담당 배정", ANSWERED: "답변 완료", OPEN: "답변 대기", PENDING: "확인 대기", APPROVED: "승인 완료", REJECTED: "반려", SENT: "발송 요청 접수", ACCEPTED: "발송 요청 접수", IN_APP_ONLY: "화면 알림만 제공", DRAFT: "작성 중", PUBLISHED: "게시됨", ARCHIVED: "보관됨", COMPLETED: "완료", SCHEDULED: "예약됨", EXPIRED: "만료", VERIFIED: "인증 완료", CONSUMED: "인증 사용 완료", INITIATED: "시작됨", PAYMENT_FAILED: "결제 실패", REFUNDED: "환불 완료" };
  return <StatusBadge variant={variant}>{label ?? labels[normalized] ?? normalized.replaceAll("_", " ")}</StatusBadge>;
}

export function RowAction({ children, onClick, ariaLabel }: { children: ReactNode; onClick?: () => void; ariaLabel?: string }) {
  return <Button size="small" variant="quiet" aria-label={ariaLabel} onClick={onClick}>{children}</Button>;
}

export function RowLink({
  children,
  href,
  onNavigate,
  triggerKey,
  ariaLabel,
}: {
  children: ReactNode;
  href: string;
  onNavigate?: () => void;
  triggerKey?: string;
  ariaLabel?: string;
}) {
  return (
    <a
      aria-label={ariaLabel}
      className="admin-row-link"
      data-admin-trigger={triggerKey}
      href={href}
      onClick={onNavigate ? (event) => handleAdminLinkNavigation(event, onNavigate) : undefined}
    >
      {children}
    </a>
  );
}

export function CommandDialog({
  open,
  title,
  description,
  operation,
  target,
  amount,
  confirmLabel,
  danger,
  reasonLabel = "처리 사유",
  reasonRequired = true,
  reasonMinLength = 1,
  reasonMaxLength = 500,
  reasonControl = "textarea",
  confirmDisabled = false,
  children,
  buildCommand,
  onCommand,
  onComplete,
  onRefresh,
  onClose,
}: {
  open: boolean;
  title: string;
  description: string;
  operation: string;
  target: string;
  amount?: string;
  confirmLabel: string;
  danger?: boolean;
  reasonLabel?: string;
  reasonRequired?: boolean;
  reasonMinLength?: number;
  reasonMaxLength?: number;
  reasonControl?: "textarea" | "password";
  confirmDisabled?: boolean;
  children?: ReactNode;
  buildCommand: (reason: string) => Command;
  onCommand: (command: Command) => Promise<unknown>;
  onComplete?: (result: unknown) => void | Promise<void>;
  onRefresh?: () => void | Promise<void>;
  onClose: () => void;
}) {
  const formId = useId();
  const reasonRef = useRef<FieldElement>(null);
  const [reason,setReason] = useState("");
  const [refreshing,setRefreshing] = useState(false);
  const command = useCommandState();
  const { state: commandState } = command;
  const pending = command.isSubmitting || refreshing;
  const complete = command.isComplete;
  const blocked = command.isBlocked;
  const error = commandState.error;
  const resultStatus = commandState.resultStatus;
  useEffect(() => {
    if (!open) {
      setReason("");
      setRefreshing(false);
      command.reset();
    }
  }, [command.reset, open]);
  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (pending || blocked || complete || confirmDisabled) return;
    if (reasonRequired && reason.trim().length < reasonMinLength) {
      command.setValidationError(reasonMinLength > 1
        ? `${reasonLabel}를 ${reasonMinLength}자 이상 입력해 주세요.`
        : `${reasonLabel}를 입력해 주세요.`);
      reasonRef.current?.focus();
      return;
    }
    await command.execute(
      () => onCommand(buildCommand(reason.trim())),
      onComplete,
    );
  };
  const close = () => {
    if (pending) return;
    setReason("");
    command.reset();
    onClose();
  };
  const refresh = async () => {
    if (!onRefresh || pending) return;
    setRefreshing(true);
    try {
      await onRefresh();
      command.reset();
      onClose();
    } catch (cause) {
      command.setReconcileError(failure(cause).message);
    } finally {
      setRefreshing(false);
    }
  };
  const errorTitle = commandState.phase === "validation-error"
    ? "입력 내용을 확인해 주세요"
    : commandState.phase === "permission-lost"
      ? "처리 권한을 확인해 주세요"
      : "처리 상태 확인 필요";
  const closeLabel = complete || blocked ? "닫기" : "취소";
  const reasonError = commandState.phase === "validation-error" && reason.trim().length < reasonMinLength ? error : undefined;
  return <Dialog open={open} closeDisabled={pending} onClose={close} size="medium" tone={danger ? "danger" : "default"} title={complete ? "요청을 접수했습니다" : title} description={complete ? "서버의 최신 처리 상태를 다시 확인해 주세요." : description}
    secondaryAction={<Button size="small" variant="quiet" disabled={pending} onClick={close}>{closeLabel}</Button>}
    primaryAction={!complete && <GuidedActionButton size="small" variant={danger ? "danger" : "primary"} blockedReason={blocked ? error ?? "직전 요청의 처리 결과가 확정되지 않았습니다. 최신 상태를 조회한 뒤 다시 시도해 주세요." : confirmDisabled ? "필수 입력 항목과 현재 업무 상태를 확인한 뒤 다시 시도해 주세요." : null} form={formId} isLoading={pending} type="submit">{confirmLabel}</GuidedActionButton>}>
    {error && <Alert persistent variant="danger" title={errorTitle} action={onRefresh && blocked ? <Button size="small" variant="quiet" isLoading={refreshing} onClick={refresh}>최신 상태 조회</Button> : undefined}>{error}</Alert>}
    {complete ? <Alert variant={resultStatus && ["FAILED","UNKNOWN"].includes(resultStatus)?"warning":"information"} title={resultStatus?"서버 처리 상태":"요청 접수 완료"}>{resultStatus ? statusBadge(resultStatus) : "최신 처리 결과를 조회한 뒤 다음 작업을 진행해 주세요."}</Alert> : <form className="admin-command" id={formId} onSubmit={submit}>
      <dl><div><dt>업무</dt><dd>{operation}</dd></div><div><dt>대상</dt><dd data-numeric>{target}</dd></div>{amount && <div><dt>금액</dt><dd data-numeric>{amount}</dd></div>}</dl>
      {children}
      {reasonRequired && (reasonControl === "password"
        ? <Field ref={reasonRef} compact autoComplete="current-password" label={reasonLabel} required type="password" value={reason} error={reasonError} onChange={(e) => setReason(e.currentTarget.value)} />
        : <Field ref={reasonRef} compact control="textarea" label={reasonLabel} required maxLength={reasonMaxLength} value={reason} error={reasonError} onChange={(e) => setReason(e.currentTarget.value)} />)}

    </form>}
  </Dialog>;
}
